import { motion } from "framer-motion";
import { Bot, Menu, X, LogOut, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/contexts/I18nContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale, t } = useI18n();

  const navItems = [
    { label: t('nav.features'), href: location.pathname === '/' ? '#features' : '/features' },
    { label: t('nav.plugins'), href: location.pathname === '/' ? '#plugins' : '/plugins' },
    { label: t('nav.premium'), href: location.pathname === '/' ? '#premium' : '/premium' },
    { label: t('nav.support'), href: location.pathname === '/' ? '#support' : '/support/help-center' },
  ];

  const handleLogin = async () => {
    try {
      console.log('Login button clicked');
      const { authService } = await import('@/lib/auth');
      await authService.signInWithDiscord();
    } catch (error: any) {
      console.error('Error initiating Discord login:', error);
      // Show error to user with more details
      const errorMessage = error?.message || 'Failed to initiate login. Please check your configuration.';
      alert(`Login Error: ${errorMessage}\n\nPlease check:\n1. VITE_API_URL is configured\n2. Backend is accessible\n3. VITE_DISCORD_CLIENT_ID is set (for fallback)`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="logo-frame w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-primary transition-all duration-300 group-hover:scale-110">
                <Bot className="w-6 h-6 text-white" />
              </div>
            </div>
            <span className="text-xl font-bold gradient-text">NATIBOT</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink key={item.label} href={item.href}>{item.label}</NavLink>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <div className="w-[110px]">
              <Select value={locale} onValueChange={(value) => setLocale(value as 'it' | 'en')}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t('nav.language')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <NotificationBell />
                <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-muted">
                  <span className="text-sm font-medium">{user?.username}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} title={t('nav.logout')}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="text-muted-foreground hover:text-foreground" onClick={handleLogin}>
                  {t('nav.login')}
                </Button>
                <Button className="btn-gradient border-0">
                  {t('nav.addDiscord')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pt-6 pb-4 space-y-4"
          >
            {navItems.map((item) => (
              <MobileNavLink key={item.label} href={item.href} onClick={() => setIsOpen(false)}>{item.label}</MobileNavLink>
            ))}
            <div className="w-full pt-2">
              <Select value={locale} onValueChange={(value) => setLocale(value as 'it' | 'en')}>
                <SelectTrigger>
                  <SelectValue placeholder={t('nav.language')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">Italiano</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="pt-4 space-y-3">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="block">
                    <Button variant="outline" className="w-full">{t('nav.dashboard')}</Button>
                  </Link>
                  <Button variant="outline" className="w-full" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" className="w-full" onClick={handleLogin}>
                    {t('nav.login')}
                  </Button>
                  <Button className="w-full btn-gradient border-0">{t('nav.addDiscord')}</Button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

const NavLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
  href.startsWith('#') ? (
    <a 
      href={href} 
      className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium"
    >
      {children}
    </a>
  ) : (
    <Link
      to={href}
      className="text-muted-foreground hover:text-foreground transition-colors duration-200 text-sm font-medium"
    >
      {children}
    </Link>
  )
);

const MobileNavLink = ({ href, children, onClick }: { href: string; children: React.ReactNode; onClick: () => void }) => (
  href.startsWith('#') ? (
    <a 
      href={href} 
      onClick={onClick}
      className="block text-muted-foreground hover:text-foreground transition-colors duration-200 text-lg font-medium py-2"
    >
      {children}
    </a>
  ) : (
    <Link
      to={href}
      onClick={onClick}
      className="block text-muted-foreground hover:text-foreground transition-colors duration-200 text-lg font-medium py-2"
    >
      {children}
    </Link>
  )
);

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications] = useState<any[]>([]);
  const { t } = useI18n();

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        title={t('nav.notifications')}
      >
        <Bell className="w-4 h-4" />
        {notifications.length > 0 && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </Button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50"
        >
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold">{t('nav.notifications')}</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                {t('nav.noNotifications')}
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="p-4 border-b border-border hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <p className="font-medium text-sm">{notif.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{notif.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(notif.timestamp).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Navbar;
